import type { LobbyRequest, LobbySettingsPatch, TeamPlayersBySide } from '../../api/types';
import type { ConfigData, Team } from '../../store/config';
import { GameMode, ServerRegion, type LobbyVisibilityKey } from '../../domain/enums';

export type InviteMode = 'none' | 'flat' | 'by_side';

export type LobbyFormValues = {
  game_name: string;
  password: string;
  server_region: number;
  game_mode: number;
  league_id: number;
  priority: 'auto' | 'manual';
  cm_pick: number;
  allow_spectating: boolean;
  allchat: boolean;
  visibility: LobbyVisibilityKey;
  allow_cheats: boolean;
  /** 0 unlimited, 1 limited, 2 disabled. */
  pause_setting: number;
  /** protocol.LobbyDotaTVDelay: 0 = 10 s, 1 = 2 min, 2 = 5 min, 3 = 15 min. */
  dota_tv_delay: number;
  penalty_level_radiant: number;
  penalty_level_dire: number;
  /** Valve's series_type on a hand-run lobby: 0 none, 1 best of 3, 2 best of 5. */
  series_type: number;
  radiant_series_wins: number;
  dire_series_wins: number;
  enforce_verification_steam_ids: boolean;
  inviteMode: InviteMode;
  /** Steam IDs picked individually, used by the flat invite mode. */
  selectedPlayerIds: string[];
  radiantTeamId: string;
  direTeamId: string;
};

export function initialFormValues(config: ConfigData): LobbyFormValues {
  return {
    game_name: config.lobbyNameDefault,
    password: '',
    server_region: config.defaultServerRegion || ServerRegion.EuropeWest,
    game_mode: config.defaultGameMode || GameMode.CaptainsMode,
    league_id: config.defaultLeagueId || 0,
    priority: 'auto',
    cm_pick: 0,
    allow_spectating: true,
    allchat: false,
    visibility: 'public',
    allow_cheats: false,
    pause_setting: 0,
    dota_tv_delay: 0,
    penalty_level_radiant: 0,
    penalty_level_dire: 0,
    series_type: 0,
    radiant_series_wins: 0,
    dire_series_wins: 0,
    enforce_verification_steam_ids: true,
    inviteMode: 'none',
    selectedPlayerIds: [],
    radiantTeamId: '',
    direTeamId: '',
  };
}

/** The fields the "More settings" section owns, so a dialog can tell when it needs to open. */
export const ADVANCED_FIELDS = [
  'visibility',
  'allow_cheats',
  'pause_setting',
  'dota_tv_delay',
  'penalty_level_radiant',
  'penalty_level_dire',
  'series_type',
  'radiant_series_wins',
  'dire_series_wins',
] as const satisfies readonly (keyof LobbyFormValues)[];

export function hasAdvancedValues(values: LobbyFormValues): boolean {
  const defaults = initialFormValues({
    lobbyNameDefault: '',
    defaultLeagueId: 0,
    defaultGameMode: 0,
    defaultServerRegion: 0,
    hasPassword: false,
    players: [],
    teams: [],
  });
  return ADVANCED_FIELDS.some((field) => values[field] !== defaults[field]);
}

function teamSteamIds(teams: Team[], teamId: string): string[] {
  if (!teamId) return [];
  return teams.find((team) => team.id === teamId)?.playerSteamIds ?? [];
}

export type FormError = { field: keyof LobbyFormValues; message: string };

const MAX_SERIES_WINS = 5;

function isWinCount(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= MAX_SERIES_WINS;
}

export function validateForm(values: LobbyFormValues): FormError[] {
  const errors: FormError[] = [];

  if (!values.game_name.trim()) {
    errors.push({ field: 'game_name', message: 'A lobby name is required' });
  }
  if (values.league_id < 0) {
    errors.push({ field: 'league_id', message: 'League ID cannot be negative' });
  }
  if (!isWinCount(Number(values.radiant_series_wins)) || !isWinCount(Number(values.dire_series_wins))) {
    errors.push({ field: 'radiant_series_wins', message: `Series wins are whole numbers from 0 to ${MAX_SERIES_WINS}` });
  }
  if (values.inviteMode === 'by_side' && !values.radiantTeamId && !values.direTeamId) {
    errors.push({ field: 'radiantTeamId', message: 'Pick at least one team to invite' });
  }
  if (values.inviteMode === 'flat' && values.selectedPlayerIds.length === 0) {
    errors.push({ field: 'selectedPlayerIds', message: 'Pick at least one player to invite' });
  }

  return errors;
}

function namesFor(config: ConfigData, steamIds: string[]): Record<string, string> | undefined {
  const names: Record<string, string> = {};
  for (const steamId of steamIds) {
    const name = config.players.find((player) => player.steamId === steamId)?.name?.trim();
    if (name) names[steamId] = name;
  }
  return Object.keys(names).length > 0 ? names : undefined;
}

/**
 * Builds the create payload from the form.
 *
 * Invites are mutually exclusive by design: the API treats `players` and
 * `players_by_team` as separate paths, and sending both makes the team
 * assignment ambiguous. Saved player names ride along so the bot's chat can
 * name people. Settings `lobby/create` does not take are handled by
 * buildCreateFollowUpSettings.
 */
export function buildLobbyRequest(values: LobbyFormValues, config: ConfigData): LobbyRequest {
  const payload: LobbyRequest = {
    game_name: values.game_name.trim(),
    server_region: Number(values.server_region),
    game_mode: Number(values.game_mode),
    league_id: Number(values.league_id) || 0,
    priority: values.priority,
    allow_spectating: values.allow_spectating,
    allchat: values.allchat,
  };

  const password = values.password.trim();
  if (password) payload.password = password;

  if (Number(values.game_mode) === GameMode.CaptainsMode) {
    payload.cm_pick = Number(values.cm_pick) || 0;
  }
  if (values.visibility !== 'public') payload.visibility = values.visibility;
  if (values.allow_cheats) payload.allow_cheat = true;

  let invited: string[] = [];
  if (values.inviteMode === 'flat' && values.selectedPlayerIds.length > 0) {
    payload.players = [...values.selectedPlayerIds];
    payload.enforce_verification_steam_ids = values.enforce_verification_steam_ids;
    invited = payload.players;
  }

  if (values.inviteMode === 'by_side') {
    const bySide: TeamPlayersBySide = {};
    const radiant = teamSteamIds(config.teams, values.radiantTeamId);
    const dire = teamSteamIds(config.teams, values.direTeamId);
    if (radiant.length > 0) bySide.radiant = radiant;
    if (dire.length > 0) bySide.dire = dire;
    if (bySide.radiant || bySide.dire) {
      payload.players_by_team = bySide;
      payload.enforce_verification_steam_ids = values.enforce_verification_steam_ids;
      invited = [...radiant, ...dire];
    }
  }

  const names = namesFor(config, invited);
  if (names) payload.player_names = names;

  return payload;
}

/**
 * The settings `lobby/create` ignores, to push through change-settings once
 * the lobby exists. Null when every one of them is at its default, so a plain
 * creation stays a single request.
 */
export function buildCreateFollowUpSettings(values: LobbyFormValues): LobbySettingsPatch | null {
  const patch: LobbySettingsPatch = {};
  if (Number(values.pause_setting)) patch.pause_setting = Number(values.pause_setting);
  if (Number(values.dota_tv_delay)) patch.dota_tv_delay = Number(values.dota_tv_delay);
  if (Number(values.penalty_level_radiant)) patch.penalty_level_radiant = Number(values.penalty_level_radiant);
  if (Number(values.penalty_level_dire)) patch.penalty_level_dire = Number(values.penalty_level_dire);
  if (Number(values.series_type)) {
    patch.series_type = Number(values.series_type);
    patch.radiant_series_wins = Number(values.radiant_series_wins) || 0;
    patch.dire_series_wins = Number(values.dire_series_wins) || 0;
  }
  return Object.keys(patch).length > 0 ? patch : null;
}

/** Everything the change-settings endpoint accepts for an existing lobby. */
export function buildSettingsPayload(values: LobbyFormValues): LobbySettingsPatch {
  return {
    game_name: values.game_name.trim(),
    pass_key: values.password,
    server_region: Number(values.server_region),
    game_mode: Number(values.game_mode),
    league_id: Number(values.league_id) || 0,
    allow_spectating: values.allow_spectating,
    allchat: values.allchat,
    cm_pick: Number(values.cm_pick) || 0,
    selection_priority_rules: values.priority === 'auto' ? 1 : 0,
    visibility: values.visibility,
    allow_cheats: values.allow_cheats,
    pause_setting: Number(values.pause_setting) || 0,
    dota_tv_delay: Number(values.dota_tv_delay) || 0,
    penalty_level_radiant: Number(values.penalty_level_radiant) || 0,
    penalty_level_dire: Number(values.penalty_level_dire) || 0,
    series_type: Number(values.series_type) || 0,
    radiant_series_wins: Number(values.radiant_series_wins) || 0,
    dire_series_wins: Number(values.dire_series_wins) || 0,
  };
}
